import { Button, Dropdown, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import { ColumnsType } from 'antd/es/table'
import { rupiahFormat } from 'helpers/formatter'
import { IconCaretDown, IconMoreCircle } from 'components/icons'
import IconSortAscDesc from 'components/tableHelpers/IconSortAscDesc'
import {
  EcommerceProductTypeEnum,
  OrderDirectionEnum,
  OrderOperationSortFieldEnum,
  OrdersListOperationResponseItem,
} from 'generated/api'
import { mapStatusOrder } from 'helpers/wording'

export interface PropsOrderTable {
  data: OrdersListOperationResponseItem[]
  type: EcommerceProductTypeEnum
  loading: boolean
  orderDirection: OrderDirectionEnum
  orderColumn: string
  setSortingOrder: (columnKey: OrderOperationSortFieldEnum) => void
  selectOrder: (order: OrdersListOperationResponseItem) => void
}

function OrderTable(props: PropsOrderTable) {
  const columnIndexGoodsLocation = 4
  const columnIndexDigitalProductName = 2

  const columnsOrder: ColumnsType<OrdersListOperationResponseItem> = [
    {
      title: (
        <div className="flex">
          <Typography.Text className="font-semibold text-gray-800">No.</Typography.Text>
        </div>
      ),
      width: '5%',
      dataIndex: 'nomor',
      key: 'nomor',
      render: (_text, _row, index) => (
        <div className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800">{index + 1}.</Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex">
          <Typography.Text className="font-semibold text-gray-800">Nama Pemesan</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'userName',
      key: 'userName',
      render: text => (
        <div data-testid="cell_user_name" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800 break-keep">{text}</Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex justify-between items-center hover:cursor-pointer">
          <Typography.Text className="font-semibold text-gray-800">Nomor pesanan</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: text => (
        <div data-testid="cell_order_no" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800">
            {text ? text : '-'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex justify-between items-center">
          <Typography.Text className="font-semibold text-gray-800">Tanggal Pesanan</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: dateCreatedAt => (
        <div data-testid="cell_created_at" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800 whitespace-nowrap">
            {dayjs(dateCreatedAt).format('DD MMM YYYY')}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div
          data-testid="cell_total_price_sort"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold text-gray-800">Total Tagihan</Typography.Text>
          <IconSortAscDesc
            columnName={OrderOperationSortFieldEnum.TotalPrice}
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'totalItemPrice',
      key: 'totalItemPrice',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder(OrderOperationSortFieldEnum.TotalPrice),
        }
      },
      render: text => (
        <Typography.Text data-testid="cell_total_item_price" className="font-medium break-keep">
          {rupiahFormat(text)}
        </Typography.Text>
      ),
    },
    {
      title: (
        <div
          data-testid="cell_status_sort"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold text-gray-800">Status</Typography.Text>
          <IconSortAscDesc
            columnName={OrderOperationSortFieldEnum.Status}
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'status',
      key: 'status',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder(OrderOperationSortFieldEnum.Status),
        }
      },
      render: text => {
        return (
          <Typography.Text data-testid="cell_status" className="font-medium">
            {mapStatusOrder(text)}
          </Typography.Text>
        )
      },
    },
    {
      title: (
        <Typography.Text className="font-semibold text-gray-800">
          <IconMoreCircle />
        </Typography.Text>
      ),
      width: '5%',
      key: 'action',
      render: (_text, row, index) => (
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => (
            <div className="flex flex-col gap-3 p-4 bg-white rounded-md shadow-xl">
              <span className="font-semibold pt-2 px-3">Aksi</span>
              <span
                data-testid={`select_order_detail_${index}`}
                onClick={() => props.selectOrder(row)}
                className="hover:bg-primary py-1 px-3 bg-white cursor-pointer hover:text-white rounded-lg"
              >
                Lihat Detail
              </span>
            </div>
          )}
          arrow
        >
          <Button data-testid={`button_action_${index}`} block className="bg-transparent">
            <Typography.Text className="font-medium cursor-pointer">
              <IconCaretDown />
            </Typography.Text>
          </Button>
        </Dropdown>
      ),
    },
  ]

  if (props.type === EcommerceProductTypeEnum.Goods) {
    columnsOrder.splice(columnIndexGoodsLocation, 0, {
      title: (
        <div className="flex justify-between items-center">
          <Typography.Text className="font-semibold text-gray-800">Lokasi</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'storeName',
      key: 'storeName',
      render: (storeName: string) => (
        <div data-testid="cell_store_name" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800 break-keep">
            {storeName ? storeName : '-'}
          </Typography.Text>
        </div>
      ),
    })
  } else if (props.type === EcommerceProductTypeEnum.Digital) {
    columnsOrder.splice(columnIndexDigitalProductName, 0, {
      title: (
        <div className="flex justify-between items-center">
          <Typography.Text className="font-semibold text-gray-800">Nama Produk</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'productName',
      key: 'productName',
      render: (_text, row) => (
        <div data-testid="cell_product_name" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800 break-keep">
            {
              row.orderItems.find(item => item.productType === EcommerceProductTypeEnum.Digital)
                ?.productName
            }
          </Typography.Text>
        </div>
      ),
    })
  }

  return (
    <Table
      rowKey="id"
      loading={props.loading}
      data-testid="tbl_member"
      className="table-primary table-primary--admin sticky-table-header"
      columns={columnsOrder}
      dataSource={props.data}
      pagination={false}
    />
  )
}

export default OrderTable
